// Standalone trading math — no client, no network, no subgraph shapes.
//
// Bots, backtesters and risk dashboards need these numbers without
// constructing a client. Every function is a thin marshalling layer over
// `@ostium/formulae`, the same package the contracts use; the inputs and
// outputs are plain JS numbers so nothing here needs on-chain decimal scales.

import {
  CurrentTotalProfitP,
  CurrentTotalProfitRaw,
  GetTradeLiquidationPrice,
} from '@ostium/formulae';
import { formatUsdc, formatTokens, parseUsdc, parseTokens, parseLeverage } from './data/internal/decimal.js';

export interface LiquidationPriceParams {
  /** Price the position opened at. */
  entryPx: number;
  isLong: boolean;
  /** Collateral backing the position, in USDC. */
  collateral: number;
  leverage: number;
  /** Max leverage allowed for the pair — sets the maintenance margin. */
  maxLeverage: number;
  /** Rollover fee accrued so far, in USDC. Defaults to 0 (a fresh position). */
  rollover?: number;
  /** Funding fee accrued so far, in USDC. Defaults to 0. */
  funding?: number;
}

/**
 * Price at which a position is liquidated.
 *
 * Accrued fees push the liquidation price toward the entry price, so a
 * position held for a long time liquidates sooner than it did at open.
 *
 * ```ts
 * liquidationPrice({
 *   entryPx: 100_000, isLong: true, collateral: 1000, leverage: 10, maxLeverage: 100,
 * });  // → 90_500
 * ```
 */
export function liquidationPrice(params: LiquidationPriceParams): number {
  const { entryPx, isLong, collateral, leverage, maxLeverage, rollover = 0, funding = 0 } = params;
  if (!entryPx || !collateral || !leverage) return 0;

  const px = formatTokens(
    GetTradeLiquidationPrice(
      parseTokens(entryPx).toString(),
      isLong,
      parseUsdc(collateral).toString(),
      parseLeverage(leverage).toString(),
      parseUsdc(rollover).toString(),
      parseUsdc(funding).toString(),
      parseLeverage(maxLeverage).toString(),
    ),
  );
  return px > 0 ? px : 0;
}

export interface PnLParams {
  entryPx: number;
  /** Current mark price. */
  markPx: number;
  isLong: boolean;
  collateral: number;
  leverage: number;
  /**
   * Highest leverage the position has ever run at. Defaults to `leverage`.
   * Only differs after collateral has been added or removed.
   */
  highestLeverage?: number;
  rollover?: number;
  funding?: number;
}

export interface PnLResult {
  /** Net PnL in USDC, after rollover and funding. */
  netPnl: number;
  /** Net PnL as a percentage of collateral. */
  netPnlPercent: number;
  /** `collateral + netPnl` — what the position is worth if closed now. */
  netValue: number;
}

/**
 * PnL for an open position at a given mark price.
 *
 * ```ts
 * pnl({ entryPx: 100_000, markPx: 105_000, isLong: true, collateral: 1000, leverage: 10 });
 * // → { netPnl: 500, netPnlPercent: 50, netValue: 1500 }
 * ```
 */
export function pnl(params: PnLParams): PnLResult {
  const {
    entryPx, markPx, isLong, collateral, leverage,
    highestLeverage = params.leverage, rollover = 0, funding = 0,
  } = params;
  if (!entryPx || !markPx || !collateral || !leverage) {
    return { netPnl: 0, netPnlPercent: 0, netValue: collateral || 0 };
  }

  const collateralRaw = parseUsdc(collateral).toString();
  const netPnlRaw = CurrentTotalProfitRaw(
    parseTokens(entryPx).toString(),
    parseTokens(markPx).toString(),
    isLong,
    parseLeverage(leverage).toString(),
    parseLeverage(highestLeverage).toString(),
    collateralRaw,
    parseUsdc(rollover).toString(),
    parseUsdc(funding).toString(),
  );

  const netPnl = formatUsdc(netPnlRaw);
  return {
    netPnl,
    // CurrentTotalProfitP already returns a percentage — do not rescale.
    netPnlPercent: formatUsdc(CurrentTotalProfitP(netPnlRaw, collateralRaw)),
    netValue: collateral + netPnl,
  };
}
